import React from 'react';

export function About() {
  return (
    <section id="about" className="py-20 relative overflow-hidden" style={{
      background: `linear-gradient(135deg, #8d4568 0%, #a85680 25%, #8d4568 50%, #734057 75%, #8d4568 100%)`
    }}>
      {/* Cyberpunk Grid Background */}
      <div className="absolute inset-0">
        {/* Base grid pattern adapted for pink background */}
        <div className="absolute inset-0 opacity-40">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: `
                linear-gradient(rgba(34, 211, 238, 0.15) 1px, transparent 1px),
                linear-gradient(90deg, rgba(34, 211, 238, 0.15) 1px, transparent 1px),
                linear-gradient(rgba(168, 85, 247, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(168, 85, 247, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px, 60px 60px, 20px 20px, 20px 20px',
              backgroundPosition: '0 0, 0 0, 0 0, 0 0'
            }}
          ></div>
        </div>

        {/* Enhanced neon circuit patterns for pink background */}
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-r from-transparent via-cyan-300/25 to-transparent transform -skew-y-1 shadow-lg shadow-cyan-400/20"></div>
        <div className="absolute bottom-0 right-0 w-full h-32 bg-gradient-to-l from-transparent via-orange-300/25 to-transparent transform skew-y-1 shadow-lg shadow-orange-400/20"></div>
        
        {/* Glowing orbs adapted for pink background */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-cyan-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-purple-400/20 to-orange-400/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-cyan-300/15 to-purple-300/15 rounded-full blur-3xl"></div>

        {/* Enhanced animated light rays */}
        <div className="absolute top-0 left-1/3 w-1 h-full bg-gradient-to-b from-cyan-400/40 via-transparent to-cyan-400/40 animate-pulse shadow-lg shadow-cyan-400/30"></div>
        <div className="absolute top-0 right-1/3 w-1 h-full bg-gradient-to-b from-purple-400/40 via-transparent to-purple-400/40 animate-pulse shadow-lg shadow-purple-400/30" style={{animationDelay: '1s'}}></div>
        
        {/* Enhanced corner accent elements */}
        <div className="absolute top-0 left-0 w-32 h-32">
          <div className="absolute top-0 left-0 w-16 h-1 bg-gradient-to-r from-cyan-400/80 to-transparent shadow-md shadow-cyan-400/50"></div>
          <div className="absolute top-0 left-0 w-1 h-16 bg-gradient-to-b from-cyan-400/80 to-transparent shadow-md shadow-cyan-400/50"></div>
          <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-cyan-400/60"></div>
        </div>
        <div className="absolute bottom-0 right-0 w-32 h-32">
          <div className="absolute bottom-0 right-0 w-16 h-1 bg-gradient-to-l from-orange-400/80 to-transparent shadow-md shadow-orange-400/50"></div>
          <div className="absolute bottom-0 right-0 w-1 h-16 bg-gradient-to-t from-orange-400/80 to-transparent shadow-md shadow-orange-400/50"></div>
          <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-orange-400/60"></div>
        </div>

        {/* Additional pink glow layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-pink-300/5 via-transparent to-pink-400/10 pointer-events-none"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-pink-200/5 to-transparent pointer-events-none"></div>
        
        {/* Cyberpunk data streams */}
        <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent animate-pulse"></div>
        <div className="absolute bottom-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-orange-400/50 to-transparent animate-pulse" style={{animationDelay: '2s'}}></div>
      </div>

      <style>{`
        @keyframes neon-cascade-1 {
          0%, 100% { opacity: 0.15; transform: translateY(0px); }
          33% { opacity: 1; transform: translateY(5px); }
          66% { opacity: 0.4; transform: translateY(3px); }
        }
        @keyframes neon-cascade-2 {
          0%, 100% { opacity: 0.15; transform: translateY(0px); }
          0%   { opacity: 0.15; }
          45% { opacity: 1; transform: translateY(5px); }
          75% { opacity: 0.4; transform: translateY(3px); }
        }
        @keyframes neon-cascade-3 {
          0%, 100% { opacity: 0.15; transform: translateY(0px); }
          60% { opacity: 1; transform: translateY(5px); }
          85% { opacity: 0.4; transform: translateY(3px); }
        }
      `}</style>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-8">
          {/* Tagline with enhanced styling for pink background */}
          <div className="relative inline-block mb-8">
            <div className="flex items-center justify-center space-x-3 text-orange-300 mb-4">
              <span className="text-cyan-300 opacity-90 text-lg cyberpunk-bracket-pulse shadow-lg shadow-cyan-400/30">[</span>
              <div className="relative overflow-hidden">
                <span className="text-white tracking-wider uppercase text-xl font-cyberpunk bg-gradient-to-r from-orange-300 to-cyan-300 bg-clip-text text-transparent relative z-10 cyberpunk-glow drop-shadow-lg">
                  Transporting players to extraordinary cosmic adventures
                </span>
                {/* Enhanced sheen effect for pink background */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-orange-300/30 to-transparent transform translate-x-full cyberpunk-sheen w-4 shadow-xl">
                </div>
              </div>
              <span className="text-cyan-300 opacity-90 text-lg cyberpunk-bracket-pulse shadow-lg shadow-cyan-400/30">]</span>
            </div>
            {/* Enhanced decorative underline with multiple layers */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-orange-300/80 to-transparent cyberpunk-underline"></div>
            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-cyan-300/60 to-transparent blur-sm"></div>
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-16 h-px bg-gradient-to-r from-transparent via-pink-300/40 to-transparent blur-sm"></div>
          </div>

          {/* Enhanced decorative cyberpunk frame around the tagline */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Top frame elements with enhanced glow */}
            <div className="absolute top-8 left-1/2 transform -translate-x-1/2 -translate-y-full">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-px bg-gradient-to-r from-transparent to-cyan-300/60 shadow-md shadow-cyan-400/40"></div>
                <div className="w-2 h-2 border border-cyan-300/70 rotate-45 shadow-lg shadow-cyan-400/50"></div>
                <div className="w-8 h-px bg-gradient-to-l from-transparent to-cyan-300/60 shadow-md shadow-cyan-400/40"></div>
              </div>
            </div>
            
            {/* Bottom frame elements with enhanced glow */}
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 translate-y-full">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-px bg-gradient-to-r from-transparent to-orange-300/60 shadow-md shadow-orange-400/40"></div>
                <div className="w-2 h-2 border border-orange-300/70 rotate-45 shadow-lg shadow-orange-400/50"></div>
                <div className="w-8 h-px bg-gradient-to-l from-transparent to-orange-300/60 shadow-md shadow-orange-400/40"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Pixel Art Scroll Down Arrows */}
        <div className="flex flex-col items-center gap-1">
          {[
            'neon-cascade-1 1.8s ease-in-out infinite',
            'neon-cascade-2 1.8s ease-in-out infinite',
            'neon-cascade-3 1.8s ease-in-out infinite',
          ].map((anim, i) => (
            <div key={i} style={{ animation: anim, width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{
                width: '14px',
                height: '14px',
                borderRight: '2px solid #22d3ee',
                borderBottom: '2px solid #22d3ee',
                transform: 'rotate(45deg)',
                boxShadow: '0 0 6px 1px rgba(34,211,238,0.4)',
                flexShrink: 0,
              }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}